import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, Truck, CheckCircle2 } from "lucide-react";
import { AuthAPI } from "../api/AuthAPI";
import DashboardLayout from "../layouts/DashboardLayout";

const statusStyles = {
  pending: { bg: "#EDE9FE", color: "#5B21B6" },
  confirmed: { bg: "#DBEAFE", color: "#1D4ED8" },
  processing: { bg: "#FEF3C7", color: "#D97706" },
  shipped: { bg: "#E0E7FF", color: "#4338CA" },
  delivered: { bg: "#DCFCE7", color: "#15803D" },
  cancelled: { bg: "#FEE2E2", color: "#DC2626" },
};

export default function OrderDetailsPage() {
  const { id: orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const deliveryDate = new Date(order?.delivery_date);
  const currentDate = new Date();
  const diffInHours = (currentDate - deliveryDate) / (1000 * 60 * 60);
  const canReturn = diffInHours <= 32;

  useEffect(() => {
    if (!orderId) return;
    fetchOrder();
  }, [orderId]);

  async function fetchOrder() {
    try {
      setLoading(true);
      const res = await AuthAPI.getsiglepurchaseorder(orderId);
      setOrder(res?.data?.data || null);
    } catch (err) {
      console.error(err);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-IN", {
          day: "2-digit",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  if (loading) {
    return (
   
        <div style={{ padding: "80px", textAlign: "center" }}>Loading...</div>
     
    );
  }

  if (!order) {
    return (
     
        <div style={{ padding: "80px", textAlign: "center", color: "#ef4444" }}>
          Order not found
        </div>
      
    );
  }

  const status =
    statusStyles[order.order_status?.toLowerCase()] || statusStyles.pending;

  const address = order.shipping_address || {};

  return (
    <div className="mt-14 md:mt-10 lg:mt-0">
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "26px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "6px",
              }}
            >
              Order #{order.order_number}
            </h3>

            <div style={{ fontSize: "14px", color: "#4b5563" }}>
              Placed on {formatDate(order.createdAt)}
            </div>
          </div>

          <Link
            to="/dashboard/purchase-history"
            style={{
              fontSize: "13px",
              fontWeight: "500",
              color: "#7a7b7c",
              textDecoration: "underline",
            }}
          >
            My Orders
          </Link>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "24px",
          }}
          className="order-details-grid"
        >
          {/* LEFT */}
          <div>
            {/* ORDER ITEMS */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                overflow: "hidden",
                marginBottom: "22px",
              }}
            >
              <div
                style={{
                  padding: "18px 20px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <h4 style={{ fontSize: "18px", fontWeight: "600", marginBottom: 0 }}>
                  Order Items
                </h4>
              </div>

              {order.items?.map((item, index) => (
                <div
                  key={item.id ?? index}
                  style={{
                    padding: "14px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom:
                      index !== order.items.length - 1
                        ? "1px solid #f3f4f6"
                        : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <img
                      src={item.image || "/placeholder.png"}
                      alt=""
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        border: "1px solid #eee",
                      }}
                    />

                    <div>
                      <h5
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginBottom: "4px",
                          color: "#111827",
                        }}
                      >
                        {item.product_name}
                      </h5>

                      <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: 0 }}>
                        SKU: {item.sku} | Qty: {item.qty}
                      </p>
                    </div>
                  </div>

                  <h5 style={{ fontSize: "15px", fontWeight: "700", marginBottom: 0, color: "#111827" }}>
                    ₹{Number(item.total_price).toLocaleString()}
                  </h5>
                </div>
              ))}
            </div>
<>
  <style>{`
    .order-details-grid {
      display: grid;
      grid-template-columns: 1fr; /* mobile & tablet: 1 column */
      gap: 18px;
    }

    @media (min-width: 1024px) { /* laptop and up */
      .order-details-grid {
        grid-template-columns: 1fr 1fr; /* 2 columns */
      }
    }
  `}</style>

  <div className="order-details-grid">
    {/* SHIPPING ADDRESS */}
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "14px",
        padding: "20px",
        alignSelf: "start", // prevents stretching to match tallest column
      }}
    >
      <h4 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "24px" }}>
        Shipping Address
      </h4>

      <div style={{ color: "#4b5563", lineHeight: "30px", fontSize: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <MapPin size={16} color="#EC4899" />
          <span>{address.address}</span>
        </div>

        <div style={{ paddingLeft: "26px" }}>
          {address.city}, {address.state}, {address.pincode}
          <br />
          {address.country}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px" }}>
          <Phone size={15} color="#EC4899" />
          <span>{address.phone}</span>
        </div>
      </div>
    </div>

    {/* PAYMENT DETAILS */}
    {order.payments?.length > 0 && (
      <div>
        {order.payments.map((payment, index) => (
          <div
            key={payment.id ?? index}
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "14px",
              marginBottom: "12px",
            }}
          >
            <h5 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "16px", color: "#111827" }}>
              Payment Details
            </h5>

            <Row label="Gateway" value={payment.payment_gateway?.toUpperCase()} />
            <Row label="Payment ID" value={payment.gateway_payment_id || "-"} mono />
            <Row label="Gateway Order" value={payment.gateway_order_id || "-"} mono />
            <Row label="Amount" value={`₹${Number(payment.amount).toLocaleString()}`} />

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
              <span style={{ fontSize: "13px", color: "#6b7280" }}>Status</span>
              <span
                style={{
                  background: payment.status === "success" ? "#DCFCE7" : "#FEE2E2",
                  color: payment.status === "success" ? "#15803D" : "#DC2626",
                  padding: "3px 10px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                {payment.status}
              </span>
            </div>

            <Row
              label="Paid At"
              value={payment.paid_at ? formatDate(payment.paid_at) : "-"}
            />
          </div>
        ))}
      </div>
    )}
  </div>
</>
            {/* BACK */}
            <button
              onClick={() => navigate("/dashboard/purchase-history")}
              style={{
                marginTop: "26px",
                border: "none",
                background: "transparent",
                color: "#EC4899",
                fontSize: "15px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              <ArrowLeft size={16} />
              Back to Orders
            </button>
          </div>

          {/* RIGHT */}
          <div>
            {/* STATUS */}
            

            {/* PAYMENT SUMMARY */}
            <div
              style={{
                background: "#f3f3f5",
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                padding: "22px",
              }}
            >
{/* PAYMENT SUMMARY */}

  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "26px",
    }}
  >
    <h4 style={{ fontSize: "18px", fontWeight: "600", marginBottom: 0 }}>
      Payment Summary
    </h4>

    <span
      style={{
        background: status.bg,
        color: status.color,
        padding: "6px 14px",
        borderRadius: "8px",
        fontSize: "11px",
        fontWeight: "600",
        textTransform: "uppercase",
      }}
    >
      {order.order_status}
    </span>
  </div>
            
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px", fontSize: "14px" }}>
                <span style={{ color: "#6b7280" }}>Subtotal</span>
                <span>₹{Number(order.subtotal).toLocaleString()}</span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px", fontSize: "14px" }}>
                <span style={{ color: "#6b7280" }}>Shipping</span>
                <span>₹{Number(order.shipping_charges || 0).toLocaleString()}</span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "18px", fontSize: "14px" }}>
                <span style={{ color: "#6b7280" }}>Discount</span>
                <span>- ₹{Number(order.discount_amount || 0).toLocaleString()}</span>
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "18px",
                  marginBottom: "18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h5 style={{ fontSize: "16px", fontWeight: "700", marginBottom: 0 }}>Total</h5>
                <h5 style={{ fontSize: "18px", fontWeight: "700", marginBottom: 0 }}>
                  ₹{Number(order.grand_total).toLocaleString()}
                </h5>
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>Payment</span>

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#111827",
                      textTransform: "uppercase",
                    }}
                  >
                    {order.payment_method}
                  </span>

                  <span
                    style={{
                      background: status.bg,
                      color: status.color,
                      padding: "2px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "700",
                    }}
                  >
                    {order.payment_status}
                  </span>
                </div>
              </div>
            </div>

            {/* PAYMENT DETAILS */}
          

            {/* CANCEL ORDER */}
            {order.order_status?.toLowerCase() === "pending" && (
              <button
                style={{
                  width: "100%",
                  marginTop: "20px",
                  border: "1px solid #ef4444",
                  background: "#fff",
                  color: "#ef4444",
                  borderRadius: "10px",
                  padding: "12px",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "0.3s",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#ef4444";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.color = "#ef4444";
                }}
              >
                Cancel Order
              </button>
            )}

            {/* SHIPMENT TRACKING */}
            {order.is_awb_successfully_worked && (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "14px",
                  padding: "18px",
                  marginTop: "18px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                  <Truck size={16} color="#EC4899" />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#111827" }}>
                    Shipment Tracking
                  </h5>
                </div>

                <Row label="Courier" value="Delhivery" />
                <Row label="AWB Number" value={order.awb_number} code />
                <Row
                  label="Shipped On"
                  value={order.shipped_date ? formatDate(order.shipped_date) : "Not Shipped Yet"}
                />

                <button
                  onClick={() =>
                    window.open(
                      `https://www.delhivery.com/track-v2/package/${order.awb_number}`,
                      "_blank"
                    )
                  }
                  style={{
                    width: "100%",
                    marginTop: "18px",
                    border: "none",
                    background: "#EC4899",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <MapPin size={14} />
                  Track Shipment
                </button>
              </div>
            )}

             {order.order_status?.toLowerCase() === "delivered" && (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "14px",
                  padding: "18px",
                  marginTop: "16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <CheckCircle2 size={18} color="#15803D" />
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "15px",
                      fontWeight: "700",
                      color: "#111827",
                    }}
                  >
                    Order Delivered
                  </h5>
                </div>

                <p
                  style={{
                    fontSize: "13px",
                    color: "#9ca3af",
                    margin: 0,
                  }}
                >
                  Return is not available for this order.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .order-details-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function Row({ label, value, mono, code }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginBottom: "10px" }}>
      <span style={{ fontSize: "13px", color: "#6b7280" }}>{label}</span>
      <span
        style={{
          fontSize: code ? "12px" : mono ? "12px" : "13px",
          fontWeight: "600",
          color: "#111827",
          textAlign: "right",
          wordBreak: mono ? "break-all" : "normal",
          background: code ? "#f3f4f6" : "transparent",
          padding: code ? "3px 8px" : 0,
          borderRadius: code ? "6px" : 0,
        }}
      >
        {value}
      </span>
    </div>
  );
}










